import { type Chat, formatPhone, type Member, type PublicUser } from "@rhema/client";
import {
  Avatar,
  Button,
  Card,
  ChevronLeftIcon,
  CopyIcon,
  cn,
  EmptyState,
  Eyebrow,
  focusRing,
  IconButton,
  LinkIcon,
  type MenuItem,
  MoreIcon,
  PhoneIcon,
  SegmentedControl,
  SettingRow,
  SettingsGroup,
  Sheet,
  Switch,
  Tag,
  UiLink,
  UserPlusIcon,
  VideoIcon,
  XIcon,
} from "@rhema/ui";
import { type ReactNode, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { UpgradeLink, useFailure } from "../billing/UpgradeLink";
import { chatView, firstName, initialsFor, isAdmin, toneFor } from "../data/view";
import { useMenus } from "../menus/actions";
import { leaveGroupAsk } from "../menus/confirms";
import { useFeedback } from "../menus/Feedback";
import { useItemMenu } from "../menus/useItemMenu";
import { PeoplePicker } from "../people/PeoplePicker";
import { PhotoPicker } from "../people/PhotoPicker";
import { ReportSheet } from "../people/ReportSheet";
import { SafetyNumbers } from "../people/SafetyNumbers";
import { ShareTargets } from "../people/ShareTargets";
import { contactsChanged, usePerson } from "../people/useContacts";
import { useQueravelOffered } from "../queravel/QueravelChat";
import { useApi, useClient, useClientState, useSession } from "../session/session";
import { MediaGallery } from "./MediaGallery";

type Mute = "off" | "8h" | "1w" | "always";
type Disappear = "off" | "24h" | "7d" | "90d";

const ALWAYS = "9999-12-31T23:59:59Z";
const DISAPPEAR_SECONDS: Record<Disappear, number> = {
  off: 0,
  "24h": 86_400,
  "7d": 604_800,
  "90d": 7_776_000,
};

export function muteOf(chat: Chat, now = Date.now()): Mute {
  if (!chat.muted_until) return "off";
  const until = new Date(chat.muted_until).getTime();
  if (until <= now) return "off";
  if (new Date(chat.muted_until).getFullYear() > 3000) return "always";
  return until - now > 86_400_000 ? "1w" : "8h";
}

function muteUntil(choice: Mute): string | null {
  const now = Date.now();
  if (choice === "off") return null;
  if (choice === "always") return ALWAYS;
  return new Date(now + (choice === "8h" ? 8 * 3_600_000 : 7 * 86_400_000)).toISOString();
}

function disappearOf(chat: Chat): Disappear {
  const s = chat.disappear_after ?? 0;
  if (!s) return "off";
  if (s <= 86_400) return "24h";
  if (s <= 604_800) return "7d";
  return "90d";
}

function muteDetail(chat: Chat): string {
  const m = muteOf(chat);
  if (m === "off") return "Notifications are on";
  if (m === "always") return "Muted until you turn notifications back on";
  const until = new Date(chat.muted_until ?? "");
  return `Muted until ${until.toLocaleString("en-GB", { weekday: "short", hour: "numeric", minute: "2-digit" })}`;
}

function Tile({ href, icon, label }: { href: string; icon: ReactNode; label: string }) {
  return (
    <UiLink
      href={href}
      className={cn(
        "flex h-[66px] flex-col items-center justify-center gap-1 rounded-tile bg-linen text-xs font-semibold text-ink no-underline",
        focusRing,
      )}
    >
      {icon}
      {label}
    </UiLink>
  );
}

function roleLabel(role: Member["role"]) {
  return role === "owner" ? "Owner" : role === "admin" ? "Admin" : undefined;
}

/**
 * Someone in a group's member list. Their menu opens with the "…" button, a right-click or a long
 * press, through the same person menu every member list uses. Your own row has none.
 */
function MemberRow({ member: m, me, items }: { member: Member; me: boolean; items: () => MenuItem[] }) {
  const name = m.user.display_name;
  const manageable = !me;
  const menu = useItemMenu(`Options for ${name}`, items, {
    title: name,
    detail: roleLabel(m.role) ?? `@${m.user.handle}`,
    icon: <Avatar name={name} src={m.user.avatar_url} tone={toneFor(m.user.id)} size={40} />,
  });
  return (
    <li className="relative [-webkit-touch-callout:none]" {...(manageable ? menu.bind : {})}>
      <div className="flex items-center gap-3 rounded-tile px-2 py-2">
        <Avatar name={name} src={m.user.avatar_url} tone={toneFor(m.user.id)} size={40} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[15px] font-semibold">{me ? "You" : name}</span>
          <span className="truncate text-[13px] text-ink-secondary">@{m.user.handle}</span>
        </span>
        {roleLabel(m.role) && <Tag>{roleLabel(m.role)}</Tag>}
        {manageable && (
          <IconButton
            label={`Options for ${name}`}
            variant="ghost"
            size={36}
            aria-haspopup="menu"
            aria-expanded={menu.open}
            onClick={(e) => menu.openFrom(e.currentTarget)}
          >
            <MoreIcon size={18} />
          </IconButton>
        )}
      </div>
      {menu.menu}
    </li>
  );
}

// Details and settings for a chat. A full screen on phones and a side panel on desktop.
export function ChatInfo({
  chatId,
  variant,
  onClose,
}: {
  chatId: string;
  variant: "phone" | "panel";
  onClose?: () => void;
}) {
  const client = useClient();
  const api = useApi();
  // In the iOS and Android apps the page origin belongs to the shell, so links use the server.
  const { platform } = useSession();
  const state = useClientState();
  const navigate = useNavigate();
  const chat = state.chats[chatId];
  const meId = state.me?.id;
  const view = chat ? chatView(chat, state) : null;
  const { person: peer, reload: reloadPeer } = usePerson(view?.peer?.id);
  const { error, upgrade, fail, clear } = useFailure();
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<PublicUser[]>([]);
  const [made, setInvite] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const queravel = useQueravelOffered();
  const feedback = useFeedback();
  const menus = useMenus();
  const [params, setParams] = useSearchParams();

  // Add people from the group's menu opens the picker here.
  const addAsked = params.get("add") === "1" && Boolean(chat);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the menu asks for it
  useEffect(() => {
    if (!addAsked) return;
    const p = new URLSearchParams(params);
    p.delete("add");
    setParams(p, { replace: true });
    setAdding(true);
  }, [addAsked]);

  if (!chat || !view) {
    return <EmptyState title="Chat not found" className="flex-1" />;
  }

  const group = chat.kind === "group";
  const admin = group && isAdmin(chat);
  const canAdd = group && (chat.can_add ?? admin);
  const panel = variant === "panel";

  const run = async (job: () => Promise<unknown>) => {
    setBusy(true);
    clear();
    try {
      await job();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const settings = (body: Parameters<typeof api.updateChatSettings>[1]) =>
    run(async () => {
      await api.updateChatSettings(chatId, body);
      await client.refreshChat(chatId);
    });

  const update = (body: Parameters<typeof api.updateChat>[1]) =>
    run(async () => {
      await api.updateChat(chatId, body);
      await client.refreshChat(chatId);
    });

  // A member's menu: admins change roles and remove people. Removing asks first, and starts new
  // keys, so they cannot read what the group says next.
  const memberItems = (m: Member) => () =>
    menus.person(m.user, {
      role: m.role,
      where: "group",
      onChanged: () => client.refreshChat(chatId),
      ...(admin && m.role !== "owner"
        ? {
            setRole: async (role: "admin" | "member") => {
              await api.setRole(chatId, m.user.id, role);
              await client.refreshChat(chatId);
            },
            remove: () => client.removeMember(chatId, m.user.id),
          }
        : {}),
    });

  const addPeople = () =>
    run(async () => {
      const updated = await api.addMembers(
        chatId,
        picked.map((p) => p.id),
      );
      await client.refreshChat(chatId);
      const skipped = picked.filter((p) => updated?.skipped?.includes(p.id));
      setAdding(false);
      setPicked([]);
      if (skipped.length) {
        const names = skipped.map((p) => firstName(p.display_name)).join(", ");
        fail(
          new Error(
            `${names} could not be added because of their privacy settings. Send them an invite link instead.`,
          ),
        );
      }
    });

  const createInvite = () =>
    run(async () => {
      const { code } = await api.createInvite(chatId);
      setInvite(`${platform.serverOrigin() || window.location.origin}/join/${code}`);
      setCopied(false);
    });

  const leave = () =>
    void feedback.confirm(leaveGroupAsk(view.title)).then((yes) => {
      if (!yes) return;
      void run(async () => {
        // Leaving starts new keys, so this person cannot read what the group says next.
        await client.leaveGroup(chatId);
        navigate("/chats");
      });
    });

  const toggleBlock = () =>
    run(async () => {
      if (!peer) return;
      if (peer.is_blocked) await api.unblock(peer.id);
      else await api.block(peer.id);
      reloadPeer();
      contactsChanged();
    });

  const toggleContact = () =>
    run(async () => {
      if (!peer) return;
      if (peer.is_contact) await api.removeContact(peer.id);
      else await api.addContact(peer.id);
      reloadPeer();
      contactsChanged();
    });

  const origin = platform.serverOrigin() || window.location.origin;
  const invite = made ?? (chat.invite_code ? `${origin}/join/${chat.invite_code}` : null);
  const about = group ? chat.about : (peer?.about ?? view.peer?.about);
  const phoneText = formatPhone(peer?.phone ?? view.peer?.phone);
  const subtitle = group
    ? `Group · ${chat.member_count} ${chat.member_count === 1 ? "person" : "people"}`
    : `${phoneText || `@${view.peer?.handle ?? ""}`}${view.online ? " · online" : ""}`;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen" aria-busy={busy}>
      <div className="flex flex-none flex-col items-center rounded-b-sheet bg-surface-header px-4 pt-3 pb-[22px]">
        <div className="flex self-stretch justify-between">
          {panel ? (
            <IconButton label="Close chat info" variant="linen" onClick={() => onClose?.()}>
              <XIcon size={17} strokeWidth={2.4} />
            </IconButton>
          ) : (
            <UiLink
              href={`/chats/${chat.id}`}
              aria-label="Back to conversation"
              className={cn(
                "flex h-11 w-11 items-center justify-center rounded-field bg-linen text-ink",
                focusRing,
              )}
            >
              <ChevronLeftIcon size={22} strokeWidth={2.2} />
            </UiLink>
          )}
        </div>
        <span className="-mt-1.5 rounded-full border-4 border-linen">
          <Avatar
            name={view.title}
            src={view.photo}
            tone={view.tone}
            size={100}
            serif
            shape={group ? "rounded" : "circle"}
            online={view.online}
            ringClassName="border-linen"
          />
        </span>
        <h1 className="m-0 mt-3 text-center font-serif text-[30px] font-medium tracking-[-0.02em]">
          {view.title}
        </h1>
        <p className="m-0 mt-0.5 select-text text-sm text-ink-secondary">{subtitle}</p>
        {chat.kind !== "self" && (
          <div className="mt-[18px] grid grid-cols-2 gap-2 self-stretch">
            <Tile href={`/calls/${chat.id}`} icon={<PhoneIcon size={19} />} label="Voice" />
            <Tile href={`/calls/${chat.id}`} icon={<VideoIcon size={19} />} label="Video" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 px-4 pt-3.5 pb-6">
        {error && (
          <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
            {error}
            {upgrade && <UpgradeLink />}
          </p>
        )}

        {admin && (
          <Card className="px-4 py-3.5">
            <PhotoPicker
              name={view.title}
              src={chat.avatar_url}
              initials={initialsFor(view.title)}
              tone={view.tone}
              label="the group"
              onPick={async (photo) => {
                await api.setChatPhoto(chat.id, photo);
                await client.refreshChat(chat.id);
              }}
              onRemove={async () => {
                await api.removeChatPhoto(chat.id);
                await client.refreshChat(chat.id);
              }}
            />
          </Card>
        )}

        {about && (
          <Card className="px-4 py-3.5">
            <Eyebrow>About</Eyebrow>
            <div className="mt-1 text-[15px]">{about}</div>
          </Card>
        )}

        <SettingsGroup>
          <div className="px-4 py-[13px]">
            <SettingRow title="Mute notifications" detail={muteDetail(chat)} className="px-0 pt-0 pb-2.5" />
            <SegmentedControl
              label="Mute notifications"
              value={muteOf(chat)}
              onChange={(m) => void settings({ muted_until: muteUntil(m) })}
              options={[
                { id: "off", label: "Off" },
                { id: "8h", label: "8 hours" },
                { id: "1w", label: "1 week" },
                { id: "always", label: "Always" },
              ]}
            />
          </div>
          <SettingRow
            title="Pin to the top"
            trailing={
              <Switch
                label="Pin to the top"
                checked={chat.pinned}
                onChange={(pinned) => void settings({ pinned })}
              />
            }
          />
          <SettingRow
            title="Archive"
            detail="Archived chats move out of your chat list"
            trailing={
              <Switch
                label="Archive"
                checked={chat.archived}
                onChange={(archived) => void settings({ archived })}
              />
            }
          />
          <div className="px-4 py-[13px]">
            <SettingRow
              title="Disappearing messages"
              detail={
                group && !admin
                  ? "Only admins can change this"
                  : "New messages are deleted for everyone after this long"
              }
              className="px-0 pt-0 pb-2.5"
            />
            <SegmentedControl
              label="Disappearing messages"
              value={disappearOf(chat)}
              onChange={(d) => {
                if (group && !admin) return;
                void update({ disappear_after: DISAPPEAR_SECONDS[d] });
              }}
              options={[
                { id: "off", label: "Off" },
                { id: "24h", label: "24 hours" },
                { id: "7d", label: "7 days" },
                { id: "90d", label: "90 days" },
              ]}
            />
          </div>
          {queravel && (admin || chat.kind === "direct") && (
            <SettingRow
              title={group ? "Allow Queravel in this group" : "Allow Queravel in this chat"}
              detail="Members can ask Queravel about messages here. Everyone sees a line when someone does."
              trailing={
                <Switch
                  label={group ? "Allow Queravel in this group" : "Allow Queravel in this chat"}
                  checked={chat.assistant_allowed !== false}
                  onChange={(assistant_allowed) => void update({ assistant_allowed })}
                />
              }
            />
          )}
          {admin && (
            <SettingRow
              title="Only admins can send messages"
              trailing={
                <Switch
                  label="Only admins can send messages"
                  checked={chat.only_admins_post}
                  onChange={(only_admins_post) => void update({ only_admins_post })}
                />
              }
            />
          )}
        </SettingsGroup>

        <MediaGallery chat={chat} messages={state.messages[chatId] ?? []} meId={meId} />

        {group && (
          <Card className="px-2 py-2">
            <div className="flex items-center justify-between px-2 pt-1.5 pb-1">
              <h2 className="m-0 text-[15px] font-bold">
                {chat.member_count} {chat.member_count === 1 ? "member" : "members"}
              </h2>
              {canAdd && (
                <Button
                  variant="tonal"
                  size="sm"
                  icon={<UserPlusIcon size={16} />}
                  onClick={() => setAdding(true)}
                >
                  Add people
                </Button>
              )}
            </div>
            <ul aria-label="Members" className="m-0 list-none p-0">
              {chat.members.map((m) => (
                <MemberRow key={m.user.id} member={m} me={m.user.id === meId} items={memberItems(m)} />
              ))}
            </ul>
            {chat.member_count > chat.members.length && (
              <p className="m-0 px-2 pb-1.5 text-[13px] text-ink-secondary">
                And {chat.member_count - chat.members.length} more.
              </p>
            )}
          </Card>
        )}

        {admin && (
          <Card className="px-4 py-3.5">
            <div className="flex items-center gap-2">
              <LinkIcon size={17} className="text-ink-secondary" />
              <h2 className="m-0 text-[15px] font-bold">Invite link</h2>
            </div>
            <p className="m-0 mt-1 text-[13.5px] text-ink-secondary">
              Anyone with the link can join this group. Making a new link turns off the old one.
            </p>
            {invite && (
              <div className="mt-2.5 flex items-center gap-2 rounded-field bg-surface-header py-1.5 pr-1.5 pl-3">
                <code className="min-w-0 flex-1 truncate font-sans text-sm">{invite}</code>
                <IconButton
                  label={copied ? "Link copied" : "Copy link"}
                  variant="linen"
                  size={36}
                  onClick={() =>
                    void navigator.clipboard
                      ?.writeText(invite)
                      .then(() => setCopied(true))
                      .catch(() => undefined)
                  }
                >
                  <CopyIcon size={16} />
                </IconButton>
              </div>
            )}
            {copied && (
              <p role="status" className="m-0 mt-1.5 text-[13px] text-ink-secondary">
                The link is copied.
              </p>
            )}
            {invite && (
              <div className="mt-3">
                <ShareTargets
                  text={`Join ${view.title} on Rhema: ${invite}`}
                  url={invite}
                  subject={`Join ${view.title} on Rhema`}
                  onResult={(r) => r.note && setCopied(r.note.startsWith("Copied"))}
                />
              </div>
            )}
            <Button variant="tonal" size="sm" className="mt-2.5" onClick={() => void createInvite()}>
              {invite ? "Make a new link" : "Create invite link"}
            </Button>
          </Card>
        )}

        {!group && view.peer && <SafetyNumbers userId={view.peer.id} name={view.title} />}

        {!group && peer && (
          <div className="flex flex-col gap-2">
            <Button variant="outline" className="h-12 rounded-tile" onClick={() => void toggleContact()}>
              {peer.is_contact ? "Remove from contacts" : "Add to contacts"}
            </Button>
            <Button
              variant="danger"
              className="h-12 rounded-tile text-[15px]"
              onClick={() => void toggleBlock()}
            >
              {peer.is_blocked
                ? `Unblock ${firstName(peer.display_name)}`
                : `Block ${firstName(peer.display_name)}`}
            </Button>
          </div>
        )}

        {group && (
          <Button variant="danger" className="h-12 rounded-tile text-[15px]" onClick={leave}>
            Leave group
          </Button>
        )}

        {(group || peer) && (
          <Button
            variant="ghost"
            className="self-center text-ink-secondary"
            onClick={() => setReporting(true)}
          >
            {group ? "Report this group" : `Report ${firstName(peer?.display_name ?? view.title)}`}
          </Button>
        )}
        {reported && (
          <p role="status" className="m-0 text-center text-[13px] text-ink-secondary">
            Thank you. We will look at your report.
          </p>
        )}
      </div>

      <ReportSheet
        open={reporting}
        onClose={(sent) => {
          setReporting(false);
          if (!sent) return;
          setReported(true);
          reloadPeer();
          contactsChanged();
        }}
        target={{
          ...(group ? {} : peer ? { userId: peer.id } : {}),
          chatId: chat.id,
          name: group ? view.title : firstName(peer?.display_name ?? view.title),
        }}
      />

      <Sheet
        open={adding}
        onClose={() => {
          setAdding(false);
          setPicked([]);
        }}
        title="Add people"
        subtitle={picked.length ? `${picked.length} chosen` : "Choose who to add"}
        className="px-5 pb-7"
      >
        <PeoplePicker
          multiple
          selected={picked.map((p) => p.id)}
          exclude={chat.members.map((m) => m.user.id)}
          onPick={(p) =>
            setPicked((list) =>
              list.some((x) => x.id === p.id) ? list.filter((x) => x.id !== p.id) : [...list, p],
            )
          }
        />
        <Button
          size="lg"
          block
          className="mt-4"
          disabled={!picked.length || busy}
          onClick={() => void addPeople()}
        >
          {picked.length ? `Add ${picked.length} ${picked.length === 1 ? "person" : "people"}` : "Add people"}
        </Button>
      </Sheet>
    </div>
  );
}
